import { Delaunay } from "d3-delaunay";

/** LCG seeded from a string – same pattern used across the project. */
export function seededRNG(seed: string): () => number {
    let s = [...seed].reduce((a, c) => a + c.charCodeAt(0), 0) || 1;
    return () => {
        s = (s * 9301 + 49297) % 233280;
        return s / 233280;
    };
}

export function polygonToPathD(pts: ReadonlyArray<readonly [number, number]>): string {
    if (pts.length === 0) return "";
    const [x0, y0] = pts[0];
    let d = `M ${x0} ${y0}`;
    for (let i = 1; i < pts.length; i++) {
        d += ` L ${pts[i][0]} ${pts[i][1]}`;
    }
    return `${d} Z`;
}

export function generateRandomSites(
    count: number,
    w: number,
    h: number,
    rng: () => number,
): [number, number][] {
    const pts: [number, number][] = [];
    for (let i = 0; i < count; i++) {
        pts.push([rng() * w, rng() * h]);
    }
    return pts;
}

export function generateGridSites(
    count: number,
    w: number,
    h: number,
    jitter: number,
    rng: () => number,
): [number, number][] {
    const cols = Math.max(1, Math.ceil(Math.sqrt(count)));
    const rows = Math.max(1, Math.ceil(count / cols));
    const cw = w / cols;
    const ch = h / rows;
    const pts: [number, number][] = [];
    let n = 0;
    for (let r = 0; r < rows && n < count; r++) {
        for (let c = 0; c < cols && n < count; c++) {
            const jx = (rng() - 0.5) * 2 * jitter * cw;
            const jy = (rng() - 0.5) * 2 * jitter * ch;
            const x = (c + 0.5) * cw + jx;
            const y = (r + 0.5) * ch + jy;
            pts.push([
                Math.max(0, Math.min(w, x)),
                Math.max(0, Math.min(h, y)),
            ]);
            n++;
        }
    }
    return pts;
}

export interface VoronoiCellPath {
    pathD: string;
    index: number;
}

/** Bounded Voronoi polygons for sites in [xmin,xmax]×[ymin,ymax]. */
export function computeVoronoiCells(
    points: [number, number][],
    bounds: [number, number, number, number],
): VoronoiCellPath[] {
    const [xmin, ymin, xmax, ymax] = bounds;
    if (points.length === 0) return [];
    if (points.length === 1) {
        const poly: [number, number][] = [
            [xmin, ymin],
            [xmax, ymin],
            [xmax, ymax],
            [xmin, ymax],
        ];
        return [{ pathD: polygonToPathD(poly), index: 0 }];
    }

    const delaunay = Delaunay.from(points);
    const voronoi = delaunay.voronoi(bounds);
    const out: VoronoiCellPath[] = [];
    for (let i = 0; i < points.length; i++) {
        const poly = voronoi.cellPolygon(i);
        if (poly && poly.length >= 3) {
            const asPairs: [number, number][] = [];
            for (let j = 0; j < poly.length; j++) {
                const p = poly[j];
                asPairs.push([p[0], p[1]]);
            }
            out.push({ pathD: polygonToPathD(asPairs), index: i });
        }
    }
    return out;
}

/** Stable hue (0–360) from seed string and cell index. */
export function cellHue(seed: string, index: number): number {
    let h = [...seed].reduce((a, c) => a + c.charCodeAt(0), 0);
    h = (h + index * 47) % 360;
    return h < 0 ? h + 360 : h;
}
